/* eslint-disable react/no-children-prop */
import { GetStaticPaths, GetStaticProps } from "next";
import ReactMarkdown from "react-markdown";

import styles from "../../../styles/traits/pattern/PatternTraitsPage.module.css";

import MobileSearchPage from "@/components/publicProfiles/Header/MobileSearchPage";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import ProfileHeader from "@/components/publicProfiles/Header/ProfileHeader";
import BreadCrumbs from "@/components/shared/breadCrumbs/BreadCrumbs";
import Histogram from "@/components/traits/detailsTab/histogram/Histogram";
import { ExtendedPattern } from "@/models/sharedModels";
import patternJsonData from "../../../assets/traitInfo/patterns.json";
import AboutThisTrait from "@/components/traits/detailsTab/aboutThisTrait/AboutThisTrait";
import PatternTraitsPageLeftBar from "@/components/traits/pattern/patternsLeftBar/PatternTraitsPageLeftBar";
import OppositePattern from "@/components/traits/detailsTab/patterns/oppositePatterns/OppositePattern";
import PatternComponentOf from "@/components/traits/detailsTab/patterns/patternComponentOf/PatternComponentOf";
import Footers from "@/components/shared/Footers/Footers";
import TraitsSEOHeader from "@/components/traits/shared/TraitsSEOHeader";
import SwipeTabs from "@/components/shared/SwipeTabs/SwipeTabs";
import LargeTraitsHeading from "@/components/traits/shared/LargeTraitsHeading/LargeTraitsHeading";

type PatternTraitsPageProps = {
  pattern: ExtendedPattern;
  tabs: string[];
  markdownContent: string[];
  slicedMarkdown: string[];
};

export default function PatternTraitsPage(props: PatternTraitsPageProps) {
  const { mobileSearchOpen } = usePublicProfileContext();

  return (
    <>
      <TraitsSEOHeader
        title={`Dimensional || ${props.pattern.name}`}
        description={props.pattern.shortDescription ?? props.pattern.name}
      />
      {mobileSearchOpen === true && <MobileSearchPage />}
      <ProfileHeader />
      <div className={styles.main}>
        <div className={styles.top}>
          <BreadCrumbs
            list={["Traits", "Patterns", props.pattern.name]}
            urls={["/traits", "/traits/patterns"]}
          />
          <LargeTraitsHeading title={props.pattern.name} noMobile />
        </div>
        <div className={styles.parent}>
          <PatternTraitsPageLeftBar pattern={props.pattern} />
          <div className={styles.rightBarMain}>
            <SwipeTabs
              tabs={props.tabs}
              slicedContent={props.slicedMarkdown}
              urls={`/traits/patterns/${props.pattern.slug}`}
              content={[
                <div key={1} className={styles.markdownDiv}>
                  <ReactMarkdown children={props.markdownContent[0]} />
                </div>,
                <div key={2}>
                  <AboutThisTrait
                    dimensionSlug={props.pattern.dimensionSlugs[0]}
                    traitContent={props.pattern.traitContent}
                  />
                  <Histogram
                    // @ts-ignore
                    data={props.pattern.traitContent.histogramData}
                  />
                  {props.pattern.traitContent.oppositeDetails?.oppositeTrait
                    .slug && <OppositePattern element={props.pattern} />}
                  {props.pattern.traitContent.traitDetails && (
                    <PatternComponentOf pattern={props.pattern} />
                  )}
                </div>,
              ]}
            />
          </div>
        </div>
      </div>
      <Footers />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const list: string[] = [];
  patternJsonData.forEach((patt) => {
    if (patt.isDiscoverable === true) list.push(patt.slug);
  });
  const paths = list.map((patt) => ({
    params: { slug: patt },
  }));

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  let pattern: any;
  let slug: any;
  let tabs: string[] = ["identity", "Details"];
  let markdownContent: string[] = [];
  let slicedMarkdown: string[] = [];

  if (ctx?.params?.slug) {
    slug = ctx.params.slug;
  }

  if (slug) {
    patternJsonData.forEach((el) => {
      if (el.slug === slug) {
        pattern = el;
      }
    });
  }

  if (!pattern) {
    return {
      notFound: true,
    };
  } else {
    pattern.traitContent.markdownSections?.forEach(
      (section: any, index: number) => {
        if (!tabs.includes(section.traitSection as string)) {
          tabs.push(section.traitSection as string);
        }
        markdownContent.push(section.markdownString);
        if (index !== 0) {
          slicedMarkdown.push(section.markdownString);
        }
      }
    );
  }

  return {
    props: {
      pattern: pattern,
      tabs: tabs,
      markdownContent: markdownContent,
      slicedMarkdown: slicedMarkdown,
    },
  };
};
